import React, { useState, useEffect, useRef } from "react";
import {
  View,
  TextInput,
  FlatList,
  Text,
  TouchableOpacity,
  StyleSheet,
  StyleProp,
  ViewStyle,
  TextStyle,
} from "react-native";

export interface Prediction {
  description: string;
  place_id?: string;
}

interface PlacesAutocompleteProps {
  onPlaceSelected: (place: Prediction) => void;
  headerText?: string;
  headerStyle?: StyleProp<TextStyle>;
  placeholder?: string;
  placeholderTextColor?: string;
  value: string;
  style?: StyleProp<ViewStyle>;
  inputStyle?: string;
  error?: string;
}

const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  onPlaceSelected,  
  headerText,
  headerStyle,
  placeholder = "Address Search...",
  placeholderTextColor = "#8D8E90",
  value,
  style,
  inputStyle,
  error,
}) => {
  const [input, setInput] = useState("");
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(false);
  const isMounted = useRef(false);
  const shouldSearch = useRef(false);

  // Set value when prop changes (but don't trigger search)
  useEffect(() => {
    setInput(value);
  }, [value]);

  useEffect(() => {
    const fetchPredictions = async () => {
      if (!shouldSearch.current || input.length < 4) {
        setPredictions([]);
        return;
      }

      setLoading(true);
      try {
        // Use proxy instead of direct API call to avoid CORS issues
        const url = `${process.env.REACT_NATIVE_API_BASE_URL}gplaces/place/autocomplete/json?input=${encodeURIComponent(
          input
        )}&language=en`;

        const response = await fetch(url);
        const json = await response.json();
        setPredictions(json.predictions || []);
      } catch (error) {
        console.error("Error fetching predictions:", error);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      // Prevent first render fetch
      if (isMounted.current) {
        fetchPredictions();
      } else {
        isMounted.current = true;
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [input]);

  const handleSelect = (place: Prediction) => {
    // Prevent fetch when resetting input
    shouldSearch.current = false;
    isMounted.current = false;
    setInput(place.description);
    setPredictions([]);
    onPlaceSelected(place);
  };

  return (
    <View style={styles.container}>
      {headerText && (
        <Text style={headerStyle || styles.label}>{headerText}</Text>
      )}
      <TextInput
        value={input}
        onChangeText={(text) => {
          shouldSearch.current = true;
          setInput(text);
        }}
        placeholder={placeholder}
        placeholderTextColor={placeholderTextColor}
        className={`w-full bg-[#292D33] mt-2 p-3 text-white rounded-full ${inputStyle}`}
        style={style}
      />
      {predictions.length > 0 && (
        <FlatList
          data={predictions}
          style={{ borderRadius: 4, marginTop: 4 }}
          keyExtractor={(item) => `${item?.place_id}`}
          renderItem={({ item }) => (
            <TouchableOpacity
              onPress={() => handleSelect(item)}
              style={styles.suggestion}
            >
              <Text>{item.description}</Text>
            </TouchableOpacity>
          )}
        />
      )}
      {error ? <Text style={styles.error}>{error}</Text> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 20,
  },

  label: {
    color: "#fff",
    marginBottom: 4,
    fontWeight: "600",
  },

  suggestion: {
    padding: 10,
    borderBottomWidth: 1,
    borderColor: "#eee",
    backgroundColor: "#fff",
  },

  error: {
    color: "red",
    marginTop: 4,
  },
});

export default PlacesAutocomplete;
